import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconDownload,
  IconLoader2,
  IconFileSpreadsheet,
  IconList,
  IconListCheck,
} from '@tabler/icons-react';
import { Button } from '@core-ui/Components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@core-ui/Components/ui/dialog';
import { cn } from '@core-ui-lib/utils';
import type { ContactListType } from '../types/dashboard.types';

export type ReportType = 'original' | 'raw' | 'compact';

interface ReportOption {
  type: ReportType;
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface ReportDownloadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listType: ContactListType;
  onDownload: (type: ReportType) => Promise<void>;
}

export default function ReportDownloadModal ({
  open,
  onOpenChange,
  listType,
  onDownload,
}: ReportDownloadModalProps): React.JSX.Element {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState<ReportType | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const showOriginal = listType === 'CSV';

  const options: ReportOption[] = [
    ...(showOriginal
      ? [
        {
          type: 'original' as ReportType,
          title: t('dashboard.reportModal.originalTitle'),
          description: t('dashboard.reportModal.originalDescription'),
          icon: <IconFileSpreadsheet className="h-5 w-5" />,
        },
      ]
      : []),
    {
      type: 'raw',
      title: t('dashboard.reportModal.rawTitle'),
      description: t('dashboard.reportModal.rawDescription'),
      icon: <IconList className="h-5 w-5" />,
    },
    {
      type: 'compact',
      title: t('dashboard.reportModal.compactTitle'),
      description: t('dashboard.reportModal.compactDescription'),
      icon: <IconListCheck className="h-5 w-5" />,
    },
  ];

  const handleDownload = async (): Promise<void> => {
    if (selectedType === null) {
      return;
    }

    setIsDownloading(true);
    try {
      await onDownload(selectedType);
      onOpenChange(false);
    } finally {
      setIsDownloading(false);
      setSelectedType(null);
    }
  };

  const handleOpenChange = (open: boolean): void => {
    if (!open) {
      setSelectedType(null);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          'rounded-2xl shadow-2xl w-full max-w-sm',
          'bg-card text-foreground border-muted-foreground/30 p-0 gap-0',
          '[&>button]:text-foreground'
        )}
      >
        <DialogHeader className="p-5 pb-0">
          <DialogTitle data-qa-id="download-report-title" className="text-lg font-semibold text-foreground">
            {t('dashboard.reportModal.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="p-5 space-y-3">
          {options.map((option) => (
            <button
              data-qa-id={`${option.type}-report`}
              key={option.type}
              type="button"
              disabled={isDownloading}
              onClick={() => {
                setSelectedType(option.type); 
              }}
              className={cn(
                'w-full flex items-start gap-3 p-3 rounded-xl border transition-all text-left',
                'hover:border-primary/50 hover:bg-primary/5',
                selectedType === option.type
                  ? 'border-primary bg-primary/10 ring-1 ring-primary/30'
                  : 'border-border bg-background',
                isDownloading && 'opacity-50 cursor-not-allowed'
              )}
            >
              <div
                className={cn(
                  'flex-shrink-0 h-9 w-9 rounded-lg flex items-center justify-center',
                  selectedType === option.type
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {option.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground text-sm">
                  {option.title}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {option.description}
                </p>
              </div>
              <div
                className={cn(
                  'flex-shrink-0 h-4 w-4 rounded-full border-2 mt-0.5 transition-all',
                  selectedType === option.type
                    ? 'border-primary bg-primary'
                    : 'border-muted-foreground/40'
                )}
              >
                {selectedType === option.type && (
                  <div className="h-full w-full flex items-center justify-center">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        <div className="flex justify-end gap-2 p-5 pt-0">
          <Button
            data-qa-id="cancel-download-button"
            variant="outline"
            size="sm"
            onClick={() => {
              handleOpenChange(false); 
            }}
            disabled={isDownloading}
          >
            {t('dashboard.reportModal.cancel')}
          </Button>
          <Button
            data-qa-id="download-button"
            size="sm"
            onClick={() => {
              void handleDownload(); 
            }}
            disabled={selectedType === null || isDownloading}
            className="gap-2"
          >
            {isDownloading ? (
              <IconLoader2 className="h-4 w-4 animate-spin" />
            ) : (
              <IconDownload className="h-4 w-4" />
            )}
            {t('dashboard.reportModal.download')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface ExportButtonProps {
  onClick: () => void;
  isDownloading?: boolean;
  variant?: 'default' | 'icon';
}

export function ExportButton ({
  onClick,
  isDownloading = false,
  variant = 'default',
}: ExportButtonProps): React.JSX.Element {
  const { t } = useTranslation();

  if (variant === 'icon') {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={onClick}
        disabled={isDownloading}
        className="h-8 w-8"
      >
        {isDownloading ? (
          <IconLoader2 className="h-4 w-4 animate-spin" />
        ) : (
          <IconDownload className="h-4 w-4" />
        )}
      </Button>
    );
  }

  return (
    <Button
      data-qa-id="export-session"
      variant="outline"
      size="sm"
      onClick={onClick}
      disabled={isDownloading}
      className="gap-2"
    >
      {isDownloading ? (
        <IconLoader2 className="h-4 w-4 animate-spin" />
      ) : (
        <IconDownload className="h-4 w-4" />
      )}
      {t('dashboard.reportModal.export')}
    </Button>
  );
}
